import React, { useState, useEffect } from "react";
import {
    Paper,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    Alert,
    CircularProgress,
    Tabs,
    Tab,
    FormControl,
    Select,
    MenuItem,
    LinearProgress
} from "@mui/material";
import {
    TrendingUp as IncreaseIcon,
    TrendingDown as DecreaseIcon,
    Remove as UnchangedIcon,
    History as HistoryIcon,
    Refresh as RefreshIcon,
    Close as CloseIcon
} from "@mui/icons-material";

const PriceChangesView = ({ domain, onLoadPriceChanges, onLoadPriceHistory }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [significantChanges, setSignificantChanges] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [priceHistory, setPriceHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showHistoryDialog, setShowHistoryDialog] = useState(false);
    const [days, setDays] = useState(7);

    useEffect(() => {
        if (domain) {
            loadSignificantChanges();
        }
    }, [domain, days]);

    const loadSignificantChanges = () => {
        setLoading(true);
        onLoadPriceChanges(domain, days, (err, data) => {
            setLoading(false);
            if (!err && data) {
                setSignificantChanges(data);
            }
        });
    };

    const handleViewHistory = (product) => {
        setSelectedProduct(product);
        setLoading(true);
        setShowHistoryDialog(true);

        onLoadPriceHistory(product._id, 30, (err, history) => {
            setLoading(false);
            if (!err && history) {
                setPriceHistory(history);
            }
        });
    };

    const handleCloseHistory = () => {
        setShowHistoryDialog(false);
        setSelectedProduct(null);
        setPriceHistory([]);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    };

    const getPriceChangeIcon = (direction) => {
        switch (direction) {
            case 'increase':
                return <IncreaseIcon color="error" />;
            case 'decrease':
                return <DecreaseIcon color="success" />;
            case 'unchanged':
                return <UnchangedIcon color="disabled" />;
            default:
                return null;
        }
    };

    const getPriceChangeColor = (direction) => {
        switch (direction) {
            case 'increase':
                return 'error';
            case 'decrease':
                return 'success';
            default:
                return 'default';
        }
    };

    const renderPriceHistoryChart = () => {
        if (!priceHistory || priceHistory.length === 0) return null;

        const prices = priceHistory.map(h => h.price?.value || 0);
        const maxPrice = Math.max(...prices);
        const minPrice = Math.min(...prices);
        const priceRange = maxPrice - minPrice;

        return (
            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                    Price Trend: {selectedProduct?.name || 'Product'}
                </Typography>
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-end', 
                    gap: 0.5, 
                    height: 200,
                    borderBottom: '2px solid',
                    borderLeft: '2px solid',
                    borderColor: 'divider',
                    px: 2,
                    pb: 2
                }}>
                    {priceHistory.map((record, index) => {
                        const price = record.price?.value || 0;
                        const heightPercent = priceRange > 0 
                            ? ((price - minPrice) / priceRange) * 100 
                            : 50;
                        const color = record.price_change?.direction === 'increase' 
                            ? 'error.main' 
                            : record.price_change?.direction === 'decrease' 
                            ? 'success.main' 
                            : 'primary.main';

                        return (
                            <Tooltip 
                                key={record._id}
                                title={`${new Date(record.recorded_at).toLocaleDateString()}: ${record.price?.currency} ${price.toFixed(2)}`}
                            >
                                <Box
                                    sx={{
                                        flex: 1,
                                        height: `${Math.max(heightPercent, 10)}%`,
                                        bgcolor: color,
                                        borderRadius: '2px 2px 0 0',
                                        cursor: 'pointer',
                                        minWidth: '20px',
                                        transition: 'opacity 0.2s',
                                        '&:hover': {
                                            opacity: 0.7
                                        }
                                    }}
                                />
                            </Tooltip>
                        );
                    })}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, px: 2 }}>
                    <Typography variant="caption" color="textSecondary">
                        {priceHistory.length} records
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        Range: {priceHistory[0]?.price?.currency || '$'} {minPrice.toFixed(2)} - {maxPrice.toFixed(2)}
                    </Typography>
                </Box>
            </Box>
        );
    };

    const renderHistoryDialog = () => {
        return (
            <Dialog open={showHistoryDialog} onClose={handleCloseHistory} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">
                            Price History
                        </Typography>
                        <IconButton onClick={handleCloseHistory}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {!loading && priceHistory.length > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Box>
                                {renderPriceHistoryChart()}
                            </Box>

                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell align="right">Price</TableCell>
                                            <TableCell align="center">Change</TableCell>
                                            <TableCell>Availability</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {priceHistory && priceHistory.map((record, index) => (
                                            <TableRow key={record._id}>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {formatDate(record.recorded_at)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {record.price?.currency} {record.price?.value?.toFixed(2)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    {record.price_change && record.price_change.direction !== 'initial' && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                                                            {getPriceChangeIcon(record.price_change.direction)}
                                                            <Chip
                                                                label={`${record.price_change.percentage > 0 ? '+' : ''}${record.price_change.percentage.toFixed(1)}%`}
                                                                size="small"
                                                                color={getPriceChangeColor(record.price_change.direction)}
                                                            />
                                                        </Box>
                                                    )}
                                                    {record.price_change?.direction === 'initial' && (
                                                        <Chip label="Initial" size="small" />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={record.availability}
                                                        size="small"
                                                        color={record.availability === 'InStock' ? 'success' : 'default'}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    {!loading && (!priceHistory || priceHistory.length === 0) && (
                        <Alert severity="info">
                            No price history available for this product
                        </Alert>
                    )}
                </DialogContent>
            </Dialog>
        );
    };

    const renderSignificantChanges = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (!significantChanges || significantChanges.length === 0) {
            return (
                <Alert severity="info">
                    No significant price changes detected in the last {days} days
                </Alert>
            );
        }

        return (
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Product</TableCell>
                            <TableCell>Brand</TableCell>
                            <TableCell align="right">Current Price</TableCell>
                            <TableCell align="center">Change</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {significantChanges && significantChanges.map((change) => (
                            <TableRow key={change._id}>
                                <TableCell>
                                    <Typography variant="body2">
                                        {change.name || 'Unnamed Product'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    {change.brand && <Chip label={change.brand} size="small" />}
                                </TableCell>
                                <TableCell align="right">
                                    <Typography variant="body2" fontWeight="bold">
                                        {change.price?.currency} {change.price?.value?.toFixed(2)}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                                        {getPriceChangeIcon(change.price_change?.direction)}
                                        <Chip
                                            label={`${change.price_change?.percentage > 0 ? '+' : ''}${change.price_change?.percentage?.toFixed(1)}%`}
                                            size="small"
                                            color={getPriceChangeColor(change.price_change?.direction)}
                                        />
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">
                                        {formatDate(change.recorded_at)}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip title="View Price History">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleViewHistory({ _id: change.scraped_product_id, name: change.name })}
                                        >
                                            <HistoryIcon />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    return (
        <Box>
            <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        Price Changes - {domain}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <FormControl size="small">
                            <Select
                                value={days}
                                onChange={(e) => setDays(e.target.value)}
                            >
                                <MenuItem value={1}>Last 24 hours</MenuItem>
                                <MenuItem value={7}>Last 7 days</MenuItem>
                                <MenuItem value={30}>Last 30 days</MenuItem>
                                <MenuItem value={90}>Last 90 days</MenuItem>
                            </Select>
                        </FormControl>
                        <Button
                            startIcon={<RefreshIcon />}
                            onClick={loadSignificantChanges}
                            disabled={loading}
                            variant="outlined"
                            size="small"
                        >
                            Refresh
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <Paper>
                {renderSignificantChanges()}
            </Paper>

            {renderHistoryDialog()}
        </Box>
    );
};

export default PriceChangesView;
